module CompanyPageView
open FSharpPlus
open Client
open CompanyPageModel
open Fable.Core
open Feliz.Bulma
open PortalLibs.Client.Components
open PortalLibs.Client.Util
open Model
open PortalLibs.Client.Components.Dropdown
open Feliz
open Fable.FontAwesome
open DisplayNumber
open PortalLibs.Shared

let searchBarLevel (left: ReactElement list) (right: ReactElement list) =
  Bulma.level [
    Bulma.levelLeft [Bulma.levelItem[prop.children left]]
    Bulma.levelRight [
      Bulma.levelItem[prop.className "report-search-col"; prop.children right]
    ]
  ]

let OnlyOneDropdownSpecific =
  OnlyOneDropdownGeneric<Company, unit, obj, obj>

let searchBar (model: CompanyPageModel.Model) dispatch =
  let companyUpdateCallback =
    React.useCallback (SelectedCompanyChanged >> dispatch, [||])

  let dateUpdateCallback =
    React.useCallback (DateRangeChanged >> dispatch, [||])

  Html.div [
    prop.className "company-tab-filters"
    prop.style [style.margin(length.px 0, length.auto, length.px(10), length.auto)]
    prop.children (
      searchBarLevel
        [
          Html.div [
            prop.style [style.width(length.px 300)]
            prop.children [
              OnlyOneDropdownSpecific
                {|
                   Title = Name "Company"
                   Items = DropdownItems.ItemListOption model.Companies
                   SelectMode =
                    SingleSelect(
                      { UpdateModel = Simple companyUpdateCallback
                        SelectedItem = Item model.SelectedCompany
                        LinkUrl = None }
                    )
                   DisplayValue = (fun m ->  $"%i{m.Co} %s{(m.Name |> Option.emptyStrIfNone)}"  )
                   Searchable = false
                   Type = Standard
                   Key = "CompanyPageCompanyDropdown" |}
            ]
          ]
          Html.div [
             prop.className "company-tab-daterange-filter-cont"
             prop.children [
                fieldWithHeader
                 None
                 []
                 "Date Range"
                 (Html.div [
                   prop.className "company-tab-daterange-filter"
                   prop.children [
                     (DatepickerBetween.DatepickerBetween
                     {| Key = "DateRange"
                        Values = model.DateRange
                        ValuesChanged = dateUpdateCallback |})
                   ]
                 ])
            ]
          ]
       ] []
    )
  ]

let totalColor = "#3273dc"

let totalColumns isTotal rows =
  let td value (format: float -> string) =
    Html.td [
      prop.style [ style.textAlign.right ]
      prop.children [
        Html.strong [
          prop.style [if JS.isFinite value && value < 0.0
                      then style.color.red
                      else if isTotal then style.color totalColor]
          prop.text(value |> format)
        ]
      ]
    ]
  [
    td (rows |> List.sumBy(fun cost -> cost.SumOfBilling)) AsCurrency.withParenthesis
    td (rows |> List.sumBy(fun cost -> cost.DirectJobCost)) AsCurrency.withParenthesis
    td (rows |> List.sumBy(fun cost -> cost.GrossMarginDollar)) AsCurrency.withParenthesis
    
    td (
      rows
      |> List.fold (fun (s, g) n -> s + n.SumOfBilling, g + n.GrossMarginDollar) (0.0, 0.0)
      |> fun (s, g) -> s / g) AsPercent.withParenthesisOrDef

    td (
      rows
      |> List.fold (fun (rev, cos) n -> rev + n.OCRev, cos + n.OCCosts) (0.0, 0.0)
      |> fun (rev, cos) -> (rev - cos) / rev) AsPercent.withParenthesisOrDef
  ]

let expandedColor = "#e6e6e6"

let TotalsRow =
  React.memo (
    "TotalsRow",
    (fun (props: {| SuperContractName: string option
                    RowExpanded: bool
                    Rows: CompanyTabRow list
                    Dispatch: Msg -> unit
                    Key: string |}) ->
      Html.tr [
        prop.className "company-page-table"
        prop.style [if props.RowExpanded then style.backgroundColor expandedColor]
        prop.onClick (fun _ -> SuperContractToggled(props.SuperContractName) |> props.Dispatch)
        prop.children [
          Html.td [
            let title =
              Html.strong (
                sprintf
                  "%s%s"
                  (props.SuperContractName |> Option.defaultValue "(No super contract details)")
                  (if props.RowExpanded then " Total" else ""))

           Html.a [
             Html.div [
               prop.style [ style.display.flex; style.justifyContent.spaceBetween ]
               prop.children [
                 title
                 Bulma.icon[
                   prop.style [ style.color "#b5b5b5" ]
                   prop.children [
                     Fa.i [ if props.RowExpanded then
                              Fa.Solid.ChevronUp
                            else
                              Fa.Solid.ChevronDown ] []
                   ]
                 ]
               ]
             ]
           ]
          ]
          yield! totalColumns false props.Rows
        ]
      ]),
    (fun (props: {| SuperContractName: string option
                    RowExpanded: bool
                    Rows: CompanyTabRow list
                    Dispatch: Msg -> unit
                    Key: string |}) -> $"TotalsRow-%s{props.Key}")
  )

let table (model: CompanyPageModel.Model) dispatch (companyData: CompanyTabRow list) =
  let companyDataGrouped =
    companyData
    |> List.filter (fun item -> item.ContractNumber <> Some "Overhead")
    |> List.groupBy (fun item -> item.SuperContractName)
    |> List.sortOptionsBy true fst 
    |>> (fun (super, rows) -> super, rows |> List.sortOptionsBy true (fun item ->  item.ContractNumber))

  let allRows = companyDataGrouped >>= snd

  let overheadRows =
    companyData
    |> List.filter (fun item -> item.ContractNumber = Some "Overhead")

  Html.div [
    prop.children [
      Bulma.table [
        table.isFullWidth; table.isHoverable
        prop.style[style.borderCollapse.separate]
        prop.children [
          Html.thead [
            Html.tr [
              prop.style(Styles.sticky())
              prop.children [
                let th (txt: string) (width: int option) = Html.th [
                  prop.style [ style.textAlign.right; style.verticalAlign.middle
                               if width.IsSome then style.width(length.px width.Value) ]
                  prop.text txt
                ]
                th "Job" None
                th "Revenue" (Some 100)
                th "Direct Job Cost" (Some 100)
                th "Gross Margin ($)" (Some 100)
                th "Gross Margin (%)" (Some 100)
                th "Original Contracts GP%" (Some 100)
              ]
            ]
          ]
          Html.tbody [
            match companyDataGrouped with
            | [] ->
              yield
                Html.tr [
                  Html.td [
                    prop.colSpan 5
                    prop.style [style.textAlign.center; style.padding(length.px 30, length.px 0)]
                    prop.text "No entries for selected date range"
                  ]
                ]
            | _ ->
              yield!
                companyDataGrouped
                >>= (fun (superContractName, rows) ->
                  [ match model.ExpandedSuperContracts
                          |> Map.tryFind superContractName
                      with
                    | None -> ()
                    | Some _ ->
                      yield!
                        rows
                        |>> (fun row ->
                          Html.tr [
                            prop.style [ style.backgroundColor expandedColor ]
                            prop.children [
                              Html.td [
                                Html.div [ 
                                  prop.style [style.paddingLeft(length.px 10)
                                              style.display.flex; style.justifyContent.spaceBetween]
                                  prop.children [
                                    Html.a [
                                      prop.onClick(goToUrl)
                                      prop.href(Pages.contractUrl row.ContractId)
                                      
                                      (sprintf "%s %s") <!> row.ContractNumber <*> row.ContractDesc
                                      |> Option.defaultValue "(No contract details)"
                                      |> prop.text
                                    ]  
                                  ]
                                ]
                              ]
                              let td value (format: float -> string) =
                                Html.td [
                                  prop.style [ style.textAlign.right
                                               if JS.isFinite value && value < 0.0 then style.color.red ]
                                  prop.text(value |> format)
                                ]
                              td row.SumOfBilling AsCurrency.withParenthesis
                              td row.DirectJobCost AsCurrency.withParenthesis
                              td row.GrossMarginDollar AsCurrency.withParenthesis
                              td (row.GrossMarginDollar / row.SumOfBilling) AsPercent.withParenthesisOrDef
                              td ((row.OCRev - row.OCCosts) / row.OCRev) AsPercent.withParenthesisOrDef
                            ]
                          ])

                    TotalsRow
                      {| SuperContractName = superContractName
                         RowExpanded = Map.containsKey superContractName model.ExpandedSuperContracts
                         Rows = rows
                         Dispatch = dispatch
                         Key = sprintf "CompanyTabTableTotalRow-%s" (superContractName |> Option.defaultValue "") |}

                    ])

              if companyDataGrouped.Length > 1 then
                yield
                  Html.tr [
                    prop.className "company-tab-total-first-total-row company-tab-total-row"
                    prop.children [
                      Html.td [
                        Html.strong [
                          prop.style [ style.color totalColor ]
                          prop.text "Company Total"
                        ]
                      ]
                      yield! totalColumns true allRows
                    ]
                  ]

              if not overheadRows.IsEmpty then
                let totalGrossMargin =
                  allRows
                  |> List.sumBy (fun cost -> cost.GrossMarginDollar)

                let totalOverhead =
                  overheadRows
                  |> List.sumBy (fun cost -> cost.DirectJobCost)

                let td value (format: float -> string) =
                  Html.td [
                    prop.style[style.textAlign.right]
                    prop.children [
                      Html.strong [
                        prop.style [if totalOverhead < 0.0 then style.color.red else style.color totalColor]
                        prop.text(value |> format)
                      ]
                    ]
                  ]
                
                yield
                  Html.tr [
                    prop.className "company-tab-total-row"
                    prop.children [
                      Html.td [
                        Html.strong [ 
                          prop.style [ style.color totalColor ]
                          prop.text "Overhead"
                        ]
                      ]
                      Html.td [ prop.style[style.textAlign.right] ]
                      Html.td [ prop.style[style.textAlign.right] ]
                      
                      td totalOverhead AsCurrency.withParenthesis
                      Html.td [ prop.style[style.textAlign.right] ]
                      Html.td [ prop.style[style.textAlign.right] ]
                    ]
                  ]

                yield
                  Html.tr [
                    prop.className "company-tab-total-row"
                    prop.children [
                      Html.td [
                        Html.strong [
                          prop.style [ style.color totalColor ]
                          prop.text "Net Income/Loss"
                        ]
                      ]
                      Html.td [ prop.style[style.textAlign.right] ]
                      Html.td [ prop.style[style.textAlign.right] ]
                      td (totalGrossMargin - totalOverhead) AsCurrency.withParenthesis
                      Html.td [ prop.style[style.textAlign.right] ]
                      Html.td [ prop.style[style.textAlign.right] ]
                    ]
                  ]
          ]
        ]
      ]
    ]
  ]


let content model dispatch =
  Html.div [
    prop.className "company-page"
    prop.style [ style.minHeight(length.px 350) ]
    prop.children [
      searchBar model dispatch
      match model.Companies, model.SelectedCompany, model.CompanyTabData with
      | Some _, None, Some []
      | Some _, None, None ->
        Html.text "Please select a Company"
        |> centeredElem
        |> elemInBox
      | _ -> loadingMessageInBoxOrContent model.CompanyTabData "Loading Company data.." (table model dispatch)
    ]
  ]


let CompanyPageView =
  React.memo (
    "CompanyPageView",
    (fun (props: {| model: Model
                    dispatch: Msg -> unit |}) ->
      Html.div [ 
        prop.className "company-page"
        prop.style [
          style.marginTop(length.px 25)
          style.maxWidth(length.px 1600)
          style.marginLeft(length.auto)
          style.marginRight(length.auto)
          style.paddingBottom(length.px 10)
        ]
        prop.children [
          content props.model props.dispatch
        ]
      ])
  )